import React, { useState, useEffect, useCallback } from "react";

import {
  InfoSmIcon as InfoSm,
  LeafIcon as Leaf,
  PausedIcon,
  Typography
} from "@packages/ds";
import moment from "moment";
import { useIntl } from "react-intl";
import { useLocation } from "react-router-dom";

import { Deployment } from "Api/organizations/projects/environments/deployments/types";
import Error from "Components/Error";
import {
  ACTIVITY_TYPES,
  ACTIVITY_CONTEXT,
  QUERY_PROJECT_BILLING
} from "Constants/constants";
import * as Accordion from "ds/Accordion";
import { Button, IconControl, Link } from "ds/Button";
import Dialog from "ds/Dialog";
import { Tooltip } from "ds/Tooltip";
import { useActivityListener } from "Hooks/useActivityListener";
import useDecodedParams from "Hooks/useDecodedParams";
import useUrls from "Hooks/useUrls";
import getUrl from "Libs/getUrl";
import {
  loadEnvironments,
  environmentsLoadingSelector
} from "Reducers/environment";
import {
  loadDeploymentCurrent,
  isLoadingSelector,
  allEnvironmentsResource,
  deploymentResourcesHandler
} from "Reducers/environment/deployment";
import { selectEnvironments } from "Reducers/environment/environment.selectors";
import { organizationEstimateSubscriptionSelector } from "Reducers/organization/organization";
import { gitProjectSelector } from "Reducers/project/project";
import * as O from "src/organization/pages/billing/components/Overview/BillingOverview.styles";
import EstimateDialog from "src/organization/pages/billing/components/Overview/EstimateDialog";
import { useProjectActivityManager } from "src/organization/pages/project/environment/ConfigureResourcesPage/hooks/useProjectActivityManager";
import { useAppSelector, useAppDispatch } from "Store/hooks";

import { ConfigureResourcesButton } from "../ConfigureResourcesButton";
import CounterRow from "../Counter";
import MeasureRow from "../Measure";

import * as S from "./ProjectBreakdown.style";

import type {
  FormattedCost,
  OrganizationOrder,
  OrganizationSubscription,
  SubscriptionEstimateComplex
} from "@packages/client";

const getAccordionItemWrappedElement = (
  options: {
    shouldWrapInAccordion: boolean;
    title: string;
    cta?: React.ReactNode;
    triggerSibling: string;
  },
  children: React.ReactNode
) => {
  if (!options.shouldWrapInAccordion) return children;

  return (
    <S.AccordionContainer>
      <S.AccordionWrapper>
        <Accordion.Root type="single" collapsible>
          <Accordion.Item value={options.title}>
            <Accordion.SectionHeader
              shouldDisableDivider
              triggerSibling={
                <S.TriggerSibling>{options.triggerSibling}</S.TriggerSibling>
              }
            >
              <S.AccordionTitleWrapper>
                {options.title} {options.cta && options.cta}
              </S.AccordionTitleWrapper>
            </Accordion.SectionHeader>
            <Accordion.Content>
              <S.AccordionItemWrapper>{children}</S.AccordionItemWrapper>
            </Accordion.Content>
          </Accordion.Item>
        </Accordion.Root>
      </S.AccordionWrapper>
    </S.AccordionContainer>
  );
};

const resourceBreakdown = (environmentResource?: Deployment) => {
  const environmentName = environmentResource?.environment_info?.name ?? "";
  const environmentType =
    environmentResource?.environment_info?.environment_type ?? "";

  const resources = deploymentResourcesHandler(environmentResource);
  const { cpu, ram, disk, status } = resources;

  return {
    environmentName,
    environmentType,
    cpu,
    ram,
    disk,
    status,
    hasResources: !!resources
  };
};

interface ProjectBreakdownProps {
  estimateCurrent?: SubscriptionEstimateComplex;
  estimateFuture?: SubscriptionEstimateComplex;
  subscription: OrganizationSubscription;
  order?: OrganizationOrder;
  asOfOrder?: OrganizationOrder;
  isTrial: boolean;
  onContinuousProfilingRemoveClick?: () => void;
  continuousProfilingCost?: FormattedCost & {
    quantity?: number;
  };
  canDowngradeContinuousProfiling?: boolean;
}

const ProjectBreakdown = ({
  subscription,
  order,
  asOfOrder,
  estimateCurrent,
  estimateFuture,
  isTrial,
  continuousProfilingCost,
  canDowngradeContinuousProfiling,
  onContinuousProfilingRemoveClick
}: ProjectBreakdownProps) => {
  const dispatch = useAppDispatch();
  const intl = useIntl();
  const location = useLocation();

  const { organizationId, projectId: urlProjectId } = useDecodedParams<{
    organizationId: string;
    projectId: string;
  }>();
  const projectId = urlProjectId || subscription.project_id;
  const isEmbedded = !estimateCurrent && estimateFuture;

  const [error, setError] = useState<{ message: string }>();
  const [shouldRetrieveResources, setShouldRetrieveResources] = useState(true);
  const [displayProjection, setDisplayProjection] = useState<
    "current" | "future"
  >(isEmbedded ? "future" : "current");
  const [showAllResourceRows, setShowAllResourceRows] = useState(false);

  const [projectSettingsUrl, projectOverviewUrl] = useUrls(
    ["organization.project.settings", "organization.project"],
    { projectId }
  );

  const project = useAppSelector(state =>
    gitProjectSelector(state, {
      organizationId,
      projectId
    })
  );
  const environments = useAppSelector(state =>
    selectEnvironments(state, { organizationId, projectId })
  );

  const mainEnvironment = Object.values(environments ?? {}).find(
    environment => environment.is_main
  );
  const resourcesForEachEnvironment = useAppSelector(state =>
    allEnvironmentsResource(state, { organizationId, projectId })
  );
  const isLoadingDeployment = useAppSelector(isLoadingSelector);
  const areEnvironmentsLoading = useAppSelector(state =>
    environmentsLoadingSelector(state, { projectId })
  );
  const orgEstimateSubscription = useAppSelector(state =>
    organizationEstimateSubscriptionSelector(state, {
      organizationId,
      subscriptionId: subscription.id
    })
  );

  const findCurrentSpend = (id: string) => {
    return (
      asOfOrder?.line_items.find(
        item => item.subscription_id === subscription.id && item.product === id
      )?.total_price_formatted ?? null
    );
  };
  const envs = Object.values(environments ?? {});
  const getEnvironmentResources = useCallback(async () => {
    try {
      setError(undefined);

      setShouldRetrieveResources(false);
      await Promise.all(
        envs.map(async env => {
          await dispatch(
            loadDeploymentCurrent({
              organizationId,
              projectId,
              environmentId: env.id,
              settings: {
                escapeLoading: true
              }
            })
          );
        })
      );
    } catch (error) {
      setError(error as { message: string });
    }
  }, [dispatch, organizationId, projectId, envs]);

  useEffect(() => {
    if (organizationId && project?.id) {
      dispatch(loadEnvironments({ organizationId, projectId: project?.id }));
    }
  }, [dispatch, organizationId, project?.id]);

  useEffect(() => {
    if (
      envs.length &&
      !isLoadingDeployment &&
      !resourcesForEachEnvironment.length &&
      !!estimateCurrent &&
      !!estimateFuture &&
      shouldRetrieveResources
    ) {
      getEnvironmentResources();
    }
  }, [
    dispatch,
    organizationId,
    projectId,
    environments,
    isLoadingDeployment,
    resourcesForEachEnvironment,
    estimateCurrent,
    estimateFuture,
    shouldRetrieveResources,
    envs.length,
    getEnvironmentResources
  ]);

  const isResourceUpdateActivityInProgress = useActivityListener({
    watchForActivityTypes: [ACTIVITY_TYPES.ENVIRONMENT.RESOURCES.UPDATE],
    context: ACTIVITY_CONTEXT.Project,
    onCompletion: () => getEnvironmentResources()
  });

  /**
   * When a user updates the resources, comes back and reloads the page, there aren't any activities for useActivityListener to listen to.
   *
   * useProjectActivityManager will watch and determine if the activities need to be fetched.
   */
  useProjectActivityManager();

  const estimate = isEmbedded
    ? estimateFuture
    : displayProjection === "current"
      ? estimateCurrent
      : estimateFuture;

  if (!estimate) return null;

  const {
    base_price,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    component_total: _component_total,
    ...components
  } = estimate.components ?? {};

  const lastRefreshed = order
    ? moment(order.last_refreshed).format("h:mma MMM D YYYY")
    : null;

  const environmentResourcesGroup =
    isLoadingDeployment ||
    areEnvironmentsLoading ||
    isResourceUpdateActivityInProgress ? (
      <S.Loading />
    ) : resourcesForEachEnvironment.length > 0 ? (
      <O.Stack>
        <S.ResourcesTable>
          <O.RowLabel>
            {intl.formatMessage({
              id: "project.billing.resources_by_environment"
            })}
          </O.RowLabel>

          <O.ColumnHeading>
            {intl.formatMessage({
              id: "project.billing.environment"
            })}
          </O.ColumnHeading>
          <O.ColumnHeading>
            {intl.formatMessage({
              id: "project.billing.type"
            })}
          </O.ColumnHeading>
          <O.ColumnHeading>
            {intl.formatMessage({
              id: "project.billing.guaranteed.cpu"
            })}
          </O.ColumnHeading>
          <O.ColumnHeading>
            {intl.formatMessage({
              id: "project.billing.shared.cpu"
            })}
          </O.ColumnHeading>
          <O.ColumnHeading>
            {intl.formatMessage({
              id: "project.billing.ram"
            })}
          </O.ColumnHeading>
          <O.ColumnHeading>
            {intl.formatMessage({
              id: "project.billing.disk"
            })}
          </O.ColumnHeading>

          <O.Separator type="minor" margin="16px 0" />

          {...resourcesForEachEnvironment
            .slice(...(showAllResourceRows ? [] : [0, 3]))
            .map(resource => {
              const {
                environmentName,
                environmentType,
                cpu,
                ram,
                disk,
                status
              } = resourceBreakdown(resource);

              const projectBillingResourcesUrl = getUrl({
                key: "organization.project.environment.configureResources",
                props: {
                  organizationId,
                  projectId,
                  environmentId: environmentName || "",
                  location: location.pathname + location.search,
                  queryParams: QUERY_PROJECT_BILLING
                }
              });

              const envName = (
                <Typography.BodyText>{environmentName}</Typography.BodyText>
              );

              const envNameComponent =
                status === "paused" ? (
                  <Tooltip
                    tooltip={intl.formatMessage({
                      id: "project.billing.paused.environment",
                      defaultMessage: "Environment is paused"
                    })}
                    align="start"
                    analyticId="project.billing.paused.environment"
                  >
                    <S.PausedIconWrapper>
                      {envName}
                      <PausedIcon color="var(--icon-granite-color,var(--granite))" />
                    </S.PausedIconWrapper>
                  </Tooltip>
                ) : (
                  envName
                );

              return (
                <React.Fragment key={`${environmentName}:${environmentType}`}>
                  <div>{envNameComponent}</div>
                  <div>
                    <S.EnvTypeText>{environmentType}</S.EnvTypeText>
                  </div>
                  <Typography.BodyText>
                    {intl.formatMessage(
                      { id: "project.billing.value_cpu" },
                      { value: cpu.guaranteed }
                    )}
                  </Typography.BodyText>
                  <Typography.BodyText>
                    {intl.formatMessage(
                      { id: "project.billing.value_cpu" },
                      { value: cpu.shared }
                    )}
                  </Typography.BodyText>
                  <Typography.BodyText>
                    {intl.formatMessage(
                      { id: "project.billing.value_gb_ram" },
                      { value: ram }
                    )}
                  </Typography.BodyText>
                  <Typography.BodyText>
                    {intl.formatMessage(
                      { id: "project.billing.value_gb" },
                      { value: disk }
                    )}
                  </Typography.BodyText>
                  <div>
                    <ConfigureResourcesButton
                      environmentConfigUrl={projectBillingResourcesUrl}
                      align="end"
                      organizationId={organizationId}
                      projectId={projectId}
                      environmentId={environmentName || ""}
                    />
                  </div>
                </React.Fragment>
              );
            })}
        </S.ResourcesTable>
        {resourcesForEachEnvironment.length > 3 && (
          <span>
            <Link
              analyticId={
                showAllResourceRows
                  ? resourcesForEachEnvironment.length === 4
                    ? "project.billing.hide_environment"
                    : "project.billing.hide_environments"
                  : "project.billing.view_all_value_environments"
              }
              href="#"
              onClick={e => {
                e.preventDefault();
                setShowAllResourceRows(value => !value);
              }}
            >
              {intl.formatMessage(
                {
                  id: showAllResourceRows
                    ? "project.billing.hide_environments"
                    : "project.billing.view_all_value_environments"
                },
                {
                  value: resourcesForEachEnvironment.length
                }
              )}
            </Link>
          </span>
        )}
      </O.Stack>
    ) : null;

  const ProjectFeeWrapper = isEmbedded ? S.ProjectFreeWrapper : "div";

  return (
    <O.ContentWrapper $isEmbedded={!!isEmbedded}>
      <O.InnerWrapper $isEmbedded={!!isEmbedded}>
        <O.Stack>
          {!isEmbedded && (
            <>
              <S.ProjectTitleWrapper>
                <O.SpacedRow>
                  <Typography.Title tag="h3">
                    {intl.formatMessage(
                      {
                        id: "organization.billing.breakdown.title",
                        defaultMessage: "billing"
                      },
                      {
                        name: orgEstimateSubscription?.project_title ?? ""
                      }
                    )}
                  </Typography.Title>
                  {subscription.green && (
                    <Dialog
                      trigger={
                        <O.IconWrapper>
                          <Leaf color="var(--theme-success-600)" />
                        </O.IconWrapper>
                      }
                      analyticId="green_discount.description"
                      title={intl.formatMessage({
                        id: "green_discount.title"
                      })}
                      description={intl.formatMessage({
                        id: "green_discount.description"
                      })}
                    />
                  )}
                </O.SpacedRow>

                <S.ActionWrapper>
                  <Button
                    variant="outline"
                    analyticId="project.billing.breakdown.project.settings"
                    to={projectSettingsUrl}
                  >
                    {intl.formatMessage({
                      id: "project.billing.breakdown.project.settings",
                      defaultMessage: "Project settings"
                    })}
                  </Button>
                  <Button variant="outline" to={projectOverviewUrl}>
                    {intl.formatMessage({
                      id: "project.billing.breakdown.project.view",
                      defaultMessage: "View project"
                    })}
                  </Button>
                </S.ActionWrapper>
              </S.ProjectTitleWrapper>

              <Error>{error?.message}</Error>

              {environmentResourcesGroup && (
                <O.PanelWrapper>{environmentResourcesGroup}</O.PanelWrapper>
              )}
            </>
          )}

          <O.PanelWrapper $isEmbedded={!!isEmbedded}>
            {isEmbedded && <O.Separator type="minor" margin="0 0 16px" />}
            <O.Stack space={32}>
              {!isEmbedded && (
                <div>
                  <O.InvoiceTitleWrapper>
                    <O.InvoiceNextTitle tag="h3">
                      {intl.formatMessage({
                        id:
                          displayProjection == "future"
                            ? "organization.billing.future_months_projection"
                            : "organization.billing.this_months_projection"
                      })}
                    </O.InvoiceNextTitle>
                    <O.BodyTextSubtle>
                      {lastRefreshed &&
                        intl.formatMessage(
                          {
                            id: "organization.billing.last_update_at_value"
                          },
                          { value: lastRefreshed }
                        )}
                    </O.BodyTextSubtle>
                    {!isEmbedded && (
                      <O.ToggleWrapper>
                        <O.Toggle
                          onPressedChange={() => {
                            setDisplayProjection("current");
                          }}
                          text={intl.formatMessage({
                            id: "organization.billing.this_month"
                          })}
                          toggleType="text"
                          pressed={displayProjection === "current"}
                        />
                        <O.Toggle
                          onPressedChange={() => {
                            setDisplayProjection("future");
                          }}
                          text={intl.formatMessage({
                            id: "organization.billing.future"
                          })}
                          toggleType="text"
                          pressed={displayProjection === "future"}
                        />
                      </O.ToggleWrapper>
                    )}
                  </O.InvoiceTitleWrapper>
                  <Typography.BodyText>
                    {intl.formatMessage({
                      id: "organization.billing.invoice.description"
                    })}
                  </Typography.BodyText>
                </div>
              )}
              <S.BreakdownTable isCurrent={displayProjection === "current"}>
                <O.ColumnHeading $spacing={isEmbedded ? "4px" : "-12px"}>
                  {intl.formatMessage({ id: "project.billing.metric" })}
                </O.ColumnHeading>
                <O.ColumnHeading $spacing={isEmbedded ? "4px" : "-12px"}>
                  {intl.formatMessage({ id: "project.billing.unit_price" })}
                </O.ColumnHeading>
                <O.ColumnHeading $spacing={isEmbedded ? "4px" : "-12px"}>
                  {intl.formatMessage({
                    id: "project.billing.quantity"
                  })}
                </O.ColumnHeading>
                {displayProjection === "current" && (
                  <O.ColumnHeading $spacing={isEmbedded ? "4px" : "-12px"}>
                    <O.DialogWrapper>
                      {intl.formatMessage({
                        id: "project.billing.current_spend"
                      })}
                      <Dialog
                        title={intl.formatMessage({
                          id: "project.billing.current_spend"
                        })}
                        description={intl.formatMessage({
                          id: "project.billing.current_spend.description"
                        })}
                        analyticId="project.billing.daily_average.description"
                        trigger={
                          <IconControl>
                            <InfoSm />
                          </IconControl>
                        }
                      />
                    </O.DialogWrapper>
                  </O.ColumnHeading>
                )}
                <O.ColumnHeading $spacing={isEmbedded ? "4px" : "-12px"}>
                  <O.DialogWrapper justifyContent="end">
                    {intl.formatMessage({
                      id: "project.billing.projected_total"
                    })}
                    <Dialog
                      align="end"
                      title={intl.formatMessage({
                        id: `organization.billing.projected_total.title.${displayProjection}`
                      })}
                      description={intl.formatMessage({
                        id: `organization.billing.projected_total.description.${displayProjection}`
                      })}
                      analyticId={`organization.billing.projected_total.description.${displayProjection}`}
                      trigger={
                        <IconControl>
                          <InfoSm />
                        </IconControl>
                      }
                    />
                  </O.DialogWrapper>
                </O.ColumnHeading>

                {!isEmbedded && <O.Separator type="minor" margin="16px 0" />}

                {isEmbedded ? (
                  <S.AccordionContainer>
                    <S.AccordionWrapper>
                      <S.AccordionItemWrapper>
                        <ProjectFeeWrapper>
                          <Typography.BodyText weight="bold">
                            {intl.formatMessage({
                              id: "project.billing.project_fee"
                            })}
                          </Typography.BodyText>
                        </ProjectFeeWrapper>
                        <div>{estimate.plan.formatted}</div>
                        <div>{estimate.plan.quantity}</div>
                        {displayProjection === "current" &&
                          (findCurrentSpend("Project fee") ??
                            estimate.plan.formatted)}
                        <O.RightText $shouldRenderSpaced={!!isEmbedded}>
                          {estimate.plan.formatted}
                        </O.RightText>
                      </S.AccordionItemWrapper>
                    </S.AccordionWrapper>
                  </S.AccordionContainer>
                ) : (
                  <>
                    <Typography.BodyText weight="bold">
                      {intl.formatMessage({
                        id: "project.billing.project_fee"
                      })}
                    </Typography.BodyText>
                    <S.BodyTextSubtleWide></S.BodyTextSubtleWide>
                    <O.BodyTextSubtle>
                      {displayProjection === "current" &&
                        (findCurrentSpend("Project fee") ??
                          estimate.plan.formatted)}
                    </O.BodyTextSubtle>
                    <O.RightText $shouldRenderSpaced={!!isEmbedded}>
                      {estimate.plan.formatted}
                    </O.RightText>
                  </>
                )}

                {!isEmbedded && <O.Separator type="minor" margin="16px 0" />}
                {!isEmbedded && (
                  <O.RowLabel>
                    {intl.formatMessage({
                      id: "project.billing.resources"
                    })}
                  </O.RowLabel>
                )}
                {getAccordionItemWrappedElement(
                  {
                    shouldWrapInAccordion: !!isEmbedded,
                    title: intl.formatMessage({
                      id: "project.billing.resources"
                    }),
                    triggerSibling: estimate.resources_total.formatted,
                    cta: (
                      <Button
                        variant="link"
                        to={getUrl({
                          key: "organization.project.environment.configureResources",
                          props: {
                            organizationId,
                            projectId,
                            environmentId: mainEnvironment?.id ?? "",
                            location: location.pathname + location.search,
                            queryParams: ""
                          }
                        })}
                      >
                        {intl.formatMessage({
                          id: "project.billing.resources.adjust"
                        })}
                      </Button>
                    )
                  },
                  <>
                    <O.RowLabelSub $isSpacingSmall={!!isEmbedded}>
                      {intl.formatMessage({
                        id: "project.billing.build_resources"
                      })}
                    </O.RowLabelSub>
                    <CounterRow
                      label={intl.formatMessage({
                        id: "project.billing.label.build_cpu"
                      })}
                      unitPrice={intl.formatMessage(
                        {
                          id: "project.billing.value_slash_hour"
                        },
                        { value: estimate.options.build_cpu.formatted }
                      )}
                      current={intl.formatMessage(
                        {
                          id: "project.billing.value_cpu_hours"
                        },
                        { value: estimate.build_cpu.current_usage.toFixed(2) }
                      )}
                      quantity={intl.formatMessage(
                        {
                          id: "project.billing.value_cpu_hours"
                        },
                        { value: estimate.build_cpu.quantity }
                      )}
                      total={estimate.build_cpu.formatted}
                      displayProjection={displayProjection}
                      isEmbedded={isEmbedded}
                      currentSpend={
                        findCurrentSpend("Build CPU") ??
                        estimate.build_cpu.formatted
                      }
                    />
                    <CounterRow
                      label={intl.formatMessage({
                        id: "project.billing.label.build_memory"
                      })}
                      unitPrice={estimate.options.build_memory.formatted}
                      current={intl.formatMessage(
                        {
                          id: "project.billing.value_tb"
                        },
                        {
                          value: estimate.build_memory.current_usage.toFixed(4)
                        }
                      )}
                      quantity={intl.formatMessage(
                        {
                          id: "project.billing.value_tb"
                        },
                        {
                          value: estimate.build_memory.quantity
                        }
                      )}
                      total={estimate.build_memory.formatted}
                      displayProjection={displayProjection}
                      isEmbedded={isEmbedded}
                      currentSpend={
                        findCurrentSpend("Build Memory") ??
                        estimate.build_memory.formatted
                      }
                    />
                    <O.RowLabelSub>
                      {intl.formatMessage({
                        id: "project.billing.application_resources"
                      })}
                    </O.RowLabelSub>
                    <MeasureRow
                      label={intl.formatMessage({
                        id: "project.billing.label.cpu_app.shared"
                      })}
                      unitPrice={estimate.options.cpu_app.formatted}
                      quantity={intl.formatMessage(
                        {
                          id: "project.billing.value_cpu"
                        },
                        { value: estimate.cpu_app.quantity }
                      )}
                      total={estimate.cpu_app.formatted}
                      displayProjection={displayProjection}
                      isEmbedded={isEmbedded}
                      currentSpend={
                        findCurrentSpend("App CPU") ??
                        estimate.cpu_app.formatted
                      }
                    />
                    <MeasureRow
                      label={intl.formatMessage({
                        id: "project.billing.label.cpu_app.guaranteed"
                      })}
                      unitPrice={
                        estimate.options.guaranteed_cpu_app?.formatted ?? ""
                      }
                      quantity={intl.formatMessage(
                        {
                          id: "project.billing.value_cpu"
                        },
                        { value: estimate.guaranteed_cpu_app.quantity }
                      )}
                      total={estimate.guaranteed_cpu_app.formatted}
                      displayProjection={displayProjection}
                      isEmbedded={isEmbedded}
                      currentSpend={
                        findCurrentSpend("Guaranteed App CPU") ??
                        estimate.guaranteed_cpu_app.formatted
                      }
                    />
                    <MeasureRow
                      label={intl.formatMessage({
                        id: "project.billing.label.memory_app"
                      })}
                      unitPrice={estimate.options.memory_app.formatted}
                      quantity={intl.formatMessage(
                        {
                          id: "project.billing.value_gb"
                        },
                        { value: estimate.memory_app.quantity }
                      )}
                      total={estimate.memory_app.formatted}
                      displayProjection={displayProjection}
                      isEmbedded={isEmbedded}
                      currentSpend={
                        findCurrentSpend("App Memory") ??
                        estimate.memory_app.formatted
                      }
                    />
                    <O.RowLabelSub>
                      {intl.formatMessage({
                        id: "project.billing.service_resources"
                      })}
                    </O.RowLabelSub>
                    <MeasureRow
                      label={intl.formatMessage({
                        id: "project.billing.label.cpu_services.shared"
                      })}
                      unitPrice={estimate.options.cpu_services.formatted}
                      quantity={intl.formatMessage(
                        {
                          id: "project.billing.value_cpu"
                        },
                        { value: estimate.cpu_services.quantity }
                      )}
                      total={estimate.cpu_services.formatted}
                      displayProjection={displayProjection}
                      isEmbedded={isEmbedded}
                      currentSpend={
                        findCurrentSpend("Services CPU") ??
                        estimate.cpu_services.formatted
                      }
                    />
                    <MeasureRow
                      label={intl.formatMessage({
                        id: "project.billing.label.cpu_services.guaranteed"
                      })}
                      unitPrice={
                        estimate.options.guaranteed_cpu_services?.formatted ??
                        ""
                      }
                      quantity={intl.formatMessage(
                        {
                          id: "project.billing.value_cpu"
                        },
                        { value: estimate.guaranteed_cpu_services.quantity }
                      )}
                      total={estimate.guaranteed_cpu_services.formatted}
                      displayProjection={displayProjection}
                      isEmbedded={isEmbedded}
                      currentSpend={
                        findCurrentSpend("Guaranteed Services CPU") ??
                        estimate.guaranteed_cpu_services.formatted
                      }
                    />
                    <MeasureRow
                      label={intl.formatMessage({
                        id: "project.billing.label.memory_services"
                      })}
                      unitPrice={estimate.options.memory_services.formatted}
                      quantity={intl.formatMessage(
                        {
                          id: "project.billing.value_gb"
                        },
                        { value: estimate.memory_services.quantity }
                      )}
                      total={estimate.memory_services.formatted}
                      displayProjection={displayProjection}
                      isEmbedded={isEmbedded}
                      currentSpend={
                        findCurrentSpend("Services Memory") ??
                        estimate.memory_services.formatted
                      }
                    />
                    <O.RowLabelSub>
                      {intl.formatMessage({
                        id: "project.billing.storage"
                      })}
                    </O.RowLabelSub>
                    <MeasureRow
                      label={intl.formatMessage({
                        id: "project.billing.label.storage_app_services"
                      })}
                      unitPrice={intl.formatMessage(
                        {
                          id: "project.billing.value_slash_gb"
                        },
                        {
                          value: estimate.options.storage_app_services.formatted
                        }
                      )}
                      quantity={intl.formatMessage(
                        {
                          id: "project.billing.value_gb"
                        },
                        { value: estimate.storage_app_services.quantity }
                      )}
                      total={estimate.storage_app_services.formatted}
                      displayProjection={displayProjection}
                      isEmbedded={isEmbedded}
                      currentSpend={
                        findCurrentSpend("Disk storage") ??
                        estimate.storage_app_services.formatted
                      }
                    />
                    <MeasureRow
                      label={intl.formatMessage({
                        id: "project.billing.label.backup_storage"
                      })}
                      unitPrice={intl.formatMessage(
                        {
                          id: "project.billing.value_slash_gb"
                        },
                        { value: estimate.options.backup_storage.formatted }
                      )}
                      quantity={intl.formatMessage(
                        {
                          id: "project.billing.value_gb"
                        },
                        { value: estimate.backup_storage.quantity }
                      )}
                      total={estimate.backup_storage.formatted}
                      displayProjection={displayProjection}
                      isEmbedded={isEmbedded}
                      currentSpend={
                        findCurrentSpend("Backup Storage") ??
                        estimate.backup_storage.formatted
                      }
                    />
                    {!isEmbedded && (
                      <>
                        <O.RowLabelSub>
                          {intl.formatMessage({
                            id: "project.billing.logs_forwarding"
                          })}
                        </O.RowLabelSub>
                        <CounterRow
                          label={intl.formatMessage({
                            id: "project.billing.label.logs_fwd_content_size"
                          })}
                          unitPrice={intl.formatMessage(
                            {
                              id: "project.billing.value_slash_tb"
                            },
                            {
                              value:
                                estimate.options.logs_fwd_content_size.formatted
                            }
                          )}
                          current={intl.formatMessage(
                            {
                              id: "project.billing.value_tb"
                            },
                            {
                              value:
                                estimate.logs_fwd_content_size.current_usage
                            }
                          )}
                          quantity={intl.formatMessage(
                            {
                              id: "project.billing.value_tb"
                            },
                            {
                              value: estimate.logs_fwd_content_size.quantity
                            }
                          )}
                          total={estimate.logs_fwd_content_size.formatted}
                          displayProjection={displayProjection}
                          isEmbedded={isEmbedded}
                          currentSpend={
                            findCurrentSpend("Logs Forwarding") ??
                            estimate.logs_fwd_content_size.formatted
                          }
                        />
                      </>
                    )}
                  </>
                )}

                {!isEmbedded && <O.Separator type="minor" margin="16px 0" />}

                {!isEmbedded && (
                  <O.RowLabel>
                    {intl.formatMessage({
                      id: "project.billing.traffic"
                    })}
                  </O.RowLabel>
                )}

                {getAccordionItemWrappedElement(
                  {
                    shouldWrapInAccordion: !!isEmbedded,
                    title: intl.formatMessage({
                      id: "project.billing.traffic"
                    }),
                    triggerSibling: `${(
                      Math.round(
                        (estimate.egress_bandwidth.amount +
                          estimate.ingress_requests.amount) *
                          100
                      ) / 100
                    ).toFixed(2)} ${estimate.egress_bandwidth.currency_symbol}`
                  },
                  <>
                    <O.RowLabelSub $isSpacingSmall={!!isEmbedded}>
                      {intl.formatMessage({
                        id: "project.billing.origin_traffic"
                      })}
                    </O.RowLabelSub>

                    <CounterRow
                      label={intl.formatMessage({
                        id: "project.billing.label.egress_bandwidth"
                      })}
                      unitPrice={intl.formatMessage(
                        {
                          id: "project.billing.value_slash_tb_after_10gb"
                        },
                        { value: estimate.options.egress_bandwidth.formatted }
                      )}
                      current={intl.formatMessage(
                        {
                          id: "project.billing.value_tb"
                        },
                        {
                          value:
                            estimate.egress_bandwidth.current_usage.toFixed(4)
                        }
                      )}
                      quantity={intl.formatMessage(
                        {
                          id: "project.billing.value_tb"
                        },
                        { value: estimate.egress_bandwidth.quantity }
                      )}
                      total={estimate.egress_bandwidth.formatted}
                      displayProjection={displayProjection}
                      isEmbedded={isEmbedded}
                      currentSpend={
                        findCurrentSpend("Origin traffic - Bandwidth") ??
                        estimate.egress_bandwidth.formatted
                      }
                    />
                    <CounterRow
                      label={intl.formatMessage({
                        id: "project.billing.label.ingress_requests"
                      })}
                      unitPrice={intl.formatMessage(
                        {
                          id: "project.billing.value_slash_1k_after_500k"
                        },
                        { value: estimate.options.ingress_requests.formatted }
                      )}
                      current={intl.formatMessage(
                        {
                          id: "project.billing.value_requests"
                        },
                        {
                          value:
                            estimate.ingress_requests.current_usage.toFixed(4)
                        }
                      )}
                      quantity={intl.formatMessage(
                        {
                          id: "project.billing.value_requests"
                        },
                        { value: estimate.ingress_requests.quantity }
                      )}
                      total={estimate.ingress_requests.formatted}
                      displayProjection={displayProjection}
                      isEmbedded={isEmbedded}
                      currentSpend={
                        findCurrentSpend("Origin traffic - Requests") ??
                        estimate.ingress_requests.formatted
                      }
                    />
                    {estimate.fastly_requests && estimate.fastly_bandwidth && (
                      <>
                        <O.RowLabelSub>
                          <span>
                            {intl.formatMessage(
                              {
                                id: "project.billing.fastly"
                              },
                              {
                                small: chunks => <small>{chunks}</small>
                              }
                            )}
                          </span>
                        </O.RowLabelSub>

                        <CounterRow
                          label={intl.formatMessage({
                            id: "project.billing.label.fastly_bandwidth"
                          })}
                          unitPrice={intl.formatMessage(
                            {
                              id: "project.billing.value_slash_tb"
                            },
                            {
                              value:
                                estimate.options.fastly_bandwidth!.formatted
                            }
                          )}
                          current={intl.formatMessage(
                            {
                              id: "project.billing.value_tb"
                            },
                            {
                              value:
                                estimate.fastly_bandwidth.current_usage.toFixed(
                                  4
                                )
                            }
                          )}
                          quantity={intl.formatMessage(
                            {
                              id: "project.billing.value_tb"
                            },
                            {
                              value: estimate.fastly_bandwidth.quantity
                            }
                          )}
                          total={estimate.fastly_bandwidth.formatted}
                          displayProjection={displayProjection}
                          isEmbedded={isEmbedded}
                          currentSpend={
                            findCurrentSpend("CDN traffic - Bandwidth") ??
                            estimate.fastly_bandwidth.formatted
                          }
                        />
                        <CounterRow
                          label={intl.formatMessage({
                            id: "project.billing.label.fastly_requests"
                          })}
                          unitPrice={intl.formatMessage(
                            {
                              id: "project.billing.value_slash_1m"
                            },
                            {
                              value: estimate.options.fastly_requests!.formatted
                            }
                          )}
                          current={intl.formatMessage(
                            {
                              id: "project.billing.value_m_requests"
                            },
                            {
                              value:
                                estimate.fastly_requests.current_usage.toFixed(
                                  4
                                )
                            }
                          )}
                          quantity={intl.formatMessage(
                            {
                              id: "project.billing.value_m_requests"
                            },
                            { value: estimate.fastly_requests.quantity }
                          )}
                          total={estimate.fastly_requests.formatted}
                          displayProjection={displayProjection}
                          isEmbedded={isEmbedded}
                          currentSpend={
                            findCurrentSpend("CDN traffic - Requests") ??
                            estimate.fastly_requests.formatted
                          }
                        />
                      </>
                    )}
                  </>
                )}

                {!isEmbedded && <O.Separator type="minor" margin="16px 0" />}

                {!isEmbedded && (
                  <O.RowLabel>
                    {intl.formatMessage({
                      id: "project.billing.project_add_ons"
                    })}
                  </O.RowLabel>
                )}

                {estimate.onboarding_setup &&
                  estimate.onboarding_setup.quantity > 0 && (
                    <>
                      <Typography.BodyText>
                        {intl.formatMessage({
                          id: "project.billing.onboarding_setup"
                        })}
                      </Typography.BodyText>
                      <O.BodyTextSubtle>
                        {estimate.options.onboarding_setup?.formatted}
                      </O.BodyTextSubtle>
                      <O.BodyTextSubtle>
                        {estimate.onboarding_setup.quantity}
                      </O.BodyTextSubtle>
                      {displayProjection === "current" && (
                        <O.BodyTextSubtle>
                          {estimate.onboarding_setup.formatted}
                        </O.BodyTextSubtle>
                      )}
                      <O.RightText>
                        {estimate.onboarding_setup.formatted}
                      </O.RightText>
                    </>
                  )}

                {isEmbedded
                  ? (continuousProfilingCost?.quantity || 0) > 0 &&
                    getAccordionItemWrappedElement(
                      {
                        shouldWrapInAccordion: !!isEmbedded,
                        title: `${intl.formatMessage({
                          id: "project.billing.project_add_ons"
                        })}: ${intl.formatMessage({
                          id: "project.billing.continuous_profiling.title"
                        })}`,
                        triggerSibling: estimate.continuous_profiling.formatted,
                        cta: canDowngradeContinuousProfiling ? (
                          <Button
                            variant="link"
                            onClick={onContinuousProfilingRemoveClick}
                          >
                            {intl.formatMessage({
                              id: "project.billing.project_add_ons.remove"
                            })}
                          </Button>
                        ) : (
                          <></>
                        )
                      },

                      <>
                        <O.RowLabelSub $isSpacingSmall={!!isEmbedded}>
                          {intl.formatMessage({
                            id: "project.billing.observability"
                          })}
                        </O.RowLabelSub>

                        <Typography.BodyText>
                          {intl.formatMessage({
                            id: "project.billing.continuous_profiling.title"
                          })}
                        </Typography.BodyText>
                        {intl.formatMessage(
                          {
                            id: "project.billing.value_slash_month"
                          },
                          {
                            value: continuousProfilingCost?.formatted
                          }
                        )}
                        <div>{continuousProfilingCost?.quantity}</div>
                        {displayProjection === "current" &&
                          (findCurrentSpend("Continuous profiling") ??
                            estimate.continuous_profiling.formatted)}
                        <O.RightText $shouldRenderSpaced={!!isEmbedded}>
                          {estimate.continuous_profiling.formatted}
                        </O.RightText>
                      </>
                    )
                  : getAccordionItemWrappedElement(
                      {
                        shouldWrapInAccordion: !!isEmbedded,
                        title: `${intl.formatMessage({
                          id: "project.billing.project_add_ons"
                        })}: ${intl.formatMessage({
                          id: "project.billing.continuous_profiling.title"
                        })}`,
                        triggerSibling: estimate.continuous_profiling.formatted,
                        cta: canDowngradeContinuousProfiling ? (
                          <Button
                            variant="link"
                            onClick={onContinuousProfilingRemoveClick}
                          >
                            {intl.formatMessage({
                              id: "project.billing.project_add_ons.remove"
                            })}
                          </Button>
                        ) : (
                          <></>
                        )
                      },

                      <>
                        <O.RowLabelSub $isSpacingSmall={!!isEmbedded}>
                          {intl.formatMessage({
                            id: "project.billing.observability"
                          })}
                        </O.RowLabelSub>

                        <Typography.BodyText>
                          {intl.formatMessage({
                            id: "project.billing.continuous_profiling.title"
                          })}
                        </Typography.BodyText>
                        {intl.formatMessage(
                          {
                            id: "project.billing.value_slash_month"
                          },
                          {
                            value: continuousProfilingCost?.formatted
                          }
                        )}
                        <div>{continuousProfilingCost?.quantity}</div>
                        {displayProjection === "current" &&
                          (findCurrentSpend("Continuous profiling") ??
                            estimate.continuous_profiling.formatted)}
                        <O.RightText $shouldRenderSpaced={!!isEmbedded}>
                          {estimate.continuous_profiling.formatted}
                        </O.RightText>
                      </>
                    )}

                {!isEmbedded && <O.Separator type="major" margin="16px 0" />}

                {base_price && (
                  <>
                    <Typography.BodyText>
                      {base_price.display_title}
                    </Typography.BodyText>
                    <S.BodyTextSubtleWide></S.BodyTextSubtleWide>
                    <O.BodyTextSubtle>
                      {(displayProjection === "current" &&
                        findCurrentSpend("Project subtotal")) ??
                        base_price.formatted}
                    </O.BodyTextSubtle>
                    <O.RightText $shouldRenderSpaced={!!isEmbedded}>
                      {base_price.formatted}
                    </O.RightText>
                  </>
                )}

                {Object.entries(components).map(([key, component]) => {
                  return (
                    <React.Fragment key={key}>
                      <Typography.BodyText>
                        {component.display_title}
                      </Typography.BodyText>
                      <S.BodyTextSubtleWide></S.BodyTextSubtleWide>
                      <O.BodyTextSubtle></O.BodyTextSubtle>
                      <O.RightText $shouldRenderSpaced={!!isEmbedded}>
                        {component.formatted}
                      </O.RightText>
                    </React.Fragment>
                  );
                })}
              </S.BreakdownTable>
              {!isEmbedded && (
                <O.Estimate
                  variant={displayProjection === "current" ? "default" : "dark"}
                  $isEmbedded={!!isEmbedded}
                >
                  <EstimateDialog
                    analyticId={`organization.billing.projected_total.description.${displayProjection}`}
                    label={intl.formatMessage({
                      id: `organization.billing.projected_total.title.${displayProjection}`
                    })}
                    tooltipTitle={intl.formatMessage({
                      id: `organization.billing.projected_total.title.${displayProjection}`
                    })}
                    tooltipDescription={intl.formatMessage({
                      id: `organization.billing.projected_total.description.${displayProjection}`
                    })}
                    total={estimate.components?.component_total.formatted}
                    description={
                      isTrial && displayProjection === "current"
                        ? intl.formatMessage({
                            id: "organization.billing.trial_discount_not_included"
                          })
                        : undefined
                    }
                  />
                </O.Estimate>
              )}
            </O.Stack>
          </O.PanelWrapper>
        </O.Stack>
      </O.InnerWrapper>
    </O.ContentWrapper>
  );
};

export default ProjectBreakdown;
